// Progress tab: one card per metric referenced by a metric-type exit criteria anywhere in the
// plan. Whole-plan daily series (GET /rtp/metric-progress, one fetch per metric) charted as a
// column chart against the per-phase threshold "staircase", marker reference lines, and a
// per-day block-target line; slim per-phase progress rows show best day vs threshold. The
// Phase/Week pills are shared across all cards and slice everything client-side.
import { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Card, Empty, Progress, Skeleton, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { phaseDateRanges } from "@utils/rtp_utils";
import type { RtpPhaseRange } from "@utils/rtp_utils";
import { formatStat } from "./CriterionForm";
import { buildWeeks } from "./DayPlansTab";
import type { WeekCell } from "./DayPlansTab";
import { useRtpMetricOptions } from "./rtpHooks";
import type { RtpMetricProgress, RtpPlanDetail } from "./types";

const { Text } = Typography;

// Highcharts can't read the .rtp-scope CSS custom properties — hexes mirror the token block
// in styles-RtpPlanner.css.
const ACCENT = "#1677ff";
const WARN = "#f59e0b";
const MUTED = "#6b7280";
const SUCCESS = "#34d399";
const VIOLET = "#8b5cf6";
const SUCCESS_TEXT = "#0f6e56";

const MARKER_COLORS: Record<string, string> = {
	baseline: MUTED,
	preInjuryBest: SUCCESS_TEXT,
	preInjuryAvg: "#0e7490",
};

// Legend names for the backend markers. An unknown key falls back to the backend's label.
const MARKER_LABELS: Record<string, string> = {
	baseline: "Baseline",
	preInjuryBest: "Pre-injury best",
	preInjuryAvg: "Pre-injury avg",
};

// One entry per reference line the chart draws. The checkbox list is built from these, so a
// checkbox shows only when its line exists, and its sample matches the line.
interface LegendItem {
	key: string;
	label: string;
	color: string;
	dashed: boolean;
}

// Light phase-band tints cycled by phase order — blue/green/purple, faint enough that the
// bars stay the focus. No amber: it's reserved for the threshold staircase.
const PHASE_BAND_COLORS = [
	"rgba(22, 119, 255, 0.06)",
	"rgba(52, 211, 153, 0.08)",
	"rgba(139, 92, 246, 0.07)",
];

const DAY_MS = 24 * 3600 * 1000;

interface PhaseThreshold {
	threshold: number;
	comparison: string;
}

interface MetricEntry {
	key: string;
	thresholds: Record<number, PhaseThreshold>;
}

interface DateWindow {
	from: Dayjs;
	to: Dayjs;
}

const dayMs = (date: string) => dayjs(date).startOf("day").valueOf();

const inWindow = (date: string, dateWindow: DateWindow | null) => {
	if (!dateWindow) return true;
	const t = dayjs(date).startOf("day");
	return !t.isBefore(dateWindow.from) && !t.isAfter(dateWindow.to);
};

// The in-phase span of one calendar-week row (null for a padding-only row).
function weekWindow(cells: WeekCell[]): DateWindow | null {
	const inPhase = cells.filter((c) => c.inPhase && c.date != null);
	if (inPhase.length === 0) return null;
	return { from: inPhase[0].date!, to: inPhase[inPhase.length - 1].date! };
}

interface MetricProgressCardProps {
	entry: MetricEntry;
	label: string;
	ranges: RtpPhaseRange[];
	dateWindow: DateWindow | null;
	progress: RtpMetricProgress | undefined;
}

const MetricProgressCard = ({ entry, label, ranges, dateWindow, progress }: MetricProgressCardProps) => {
	const scopedRanges = dateWindow ? ranges.filter((r) => !r.end.isBefore(dateWindow.from) && !r.start.isAfter(dateWindow.to)) : ranges;

	// Keys of the reference lines on the chart. Only the exit threshold starts visible; the user
	// turns the others on from the legend.
	const [shown, setShown] = useState<Set<string>>(() => new Set(["threshold"]));
	const toggleLine = (key: string, visible: boolean) =>
		setShown((prev) => {
			const next = new Set(prev);
			if (visible) next.add(key);
			else next.delete(key);
			return next;
		});

	// Per-day block targets from the Schedule: summed for volume metrics,
	// best-of otherwise (summing two blocks' top-speed targets would be nonsense).
	const targetByMs = useMemo(() => {
		const byMs = new Map<number, number>();
		if (!progress) return byMs;
		for (const r of ranges) {
			for (const block of r.phase.blocks) {
				const ms = r.start.add(block.dayIndex - 1, "day").valueOf();
				for (const t of block.content.targets) {
					const num = Number(t.target);
					if (t.metricKey !== entry.key || t.target == null || t.target === "" || Number.isNaN(num)) continue;
					const prev = byMs.get(ms);
					byMs.set(ms, progress.aggregation === "sum" ? (prev ?? 0) + num : Math.max(prev ?? -Infinity, num));
				}
			}
		}
		return byMs;
	}, [progress, ranges, entry.key]);

	const markers = useMemo(
		() => (progress?.markers ?? []).filter((m): m is typeof m & { value: number } => typeof m.value === "number"),
		[progress]
	);

	const legend: LegendItem[] = [
		{ key: "threshold", label: "Exit threshold", color: WARN, dashed: true },
		...(targetByMs.size > 0 ? [{ key: "targets", label: "Block targets", color: VIOLET, dashed: false }] : []),
		...markers.map((m) => ({
			key: m.key ?? m.label,
			label: MARKER_LABELS[m.key ?? ""] ?? m.label,
			color: MARKER_COLORS[m.key ?? ""] ?? MUTED,
			dashed: true,
		})),
	];

	const dailyOptions = useMemo<Highcharts.Options | null>(() => {
		if (!progress) return null;

		// One point per day across the whole visible span, null where the backend has no entry
		// (future days) — so the axis, phase bands, and threshold staircase cover the full plan
		// range instead of stopping at today.
		const span = dateWindow ?? { from: ranges[0].start, to: ranges[ranges.length - 1].end };
		const valueByMs = new Map(progress.dailyValues.map((d) => [dayMs(d.date), d.value]));
		const days: { ms: number; value: number | null }[] = [];
		for (let t = span.from.startOf("day"); !t.isAfter(span.to); t = t.add(1, "day")) {
			days.push({ ms: t.valueOf(), value: valueByMs.get(t.valueOf()) ?? null });
		}

		const phaseFor = (ms: number) => {
			const t = dayjs(ms);
			return ranges.find((r) => !t.isBefore(r.start) && !t.isAfter(r.end));
		};

		// Half-day padding wraps the centered columns. Bands are built for every phase — the axis
		// clips to the visible span, so a phase/week filter keeps its phase's tint (hue stays
		// stable per phase because the index is into the full ranges list).
		const bands = ranges.map((r, i) => ({
			from: r.start.valueOf() - DAY_MS / 2,
			to: r.end.valueOf() + DAY_MS / 2,
			color: PHASE_BAND_COLORS[i % PHASE_BAND_COLORS.length],
			// A short phase is only a few days wide: let its name wrap inside the band instead of
			// ending in an ellipsis.
			label: { text: r.phase.name, style: { color: MUTED, fontSize: "10px", textOverflow: "none", whiteSpace: "normal" } },
		}));

		const plotLines = markers
			.filter((m) => shown.has(m.key ?? m.label))
			.map((m) => ({
				value: m.value,
				color: MARKER_COLORS[m.key ?? ""] ?? MUTED,
				dashStyle: "ShortDash" as const,
				width: 1,
				zIndex: 4,
				// textAlign "right" ends the text at the plot edge; with the default it starts there and runs off.
				label: { text: m.label, align: "right" as const, textAlign: "right" as const, x: -4, y: -4, style: { color: MUTED, fontSize: "10px" } },
			}));

		return {
			chart: { type: "column", height: 260, backgroundColor: "transparent", spacing: [12, 8, 8, 8] },
			title: { text: undefined },
			credits: { enabled: false },
			// The ☰ export menu sat on top of the last phase's band label.
			exporting: { enabled: false },
			legend: { enabled: false },
			// Points/bands sit at LOCAL midnights (dayjs parse) — without this the axis draws its
			// ticks in UTC and every bar lands a timezone-offset right of its date tick.
			time: { useUTC: false },
			xAxis: { type: "datetime", plotBands: bands, crosshair: true },
			// softMax: the axis only auto-scales to series data, so a marker line above every
			// bar (common early in rehab) would otherwise be clipped off-chart. The 10% headroom
			// keeps the label above the highest line inside the chart.
			yAxis: {
				title: { text: null },
				plotLines,
				softMax: plotLines.length > 0 ? Math.max(...plotLines.map((l) => l.value)) * 1.1 : undefined,
			},
			tooltip: { xDateFormat: "%a, %b %e", valueDecimals: 1 },
			plotOptions: {
				column: { pointRange: DAY_MS, borderRadius: 2, maxPointWidth: 28 },
				series: { animation: false },
			},
			series: [
				{
					type: "column",
					name: label,
					// Green bar = that day already satisfies its phase's exit threshold (honoring
					// the criterion's comparison direction); blue otherwise.
					data: days.map((d) => {
						const th = phaseFor(d.ms) ? entry.thresholds[phaseFor(d.ms)!.phase.id] : undefined;
						const met =
							d.value != null &&
							th != null &&
							(th.comparison === "<=" ? d.value <= th.threshold : d.value >= th.threshold);
						return { x: d.ms, y: d.value, color: met ? SUCCESS : ACCENT };
					}),
					color: ACCENT,
				},
				...(shown.has("threshold")
					? [
							{
								type: "line" as const,
								name: "Exit threshold",
								data: days.map((d) => {
									const range = phaseFor(d.ms);
									return [d.ms, range ? (entry.thresholds[range.phase.id]?.threshold ?? null) : null];
								}),
								step: "left" as const,
								color: WARN,
								dashStyle: "Dash" as const,
								lineWidth: 2,
								marker: { enabled: false },
								enableMouseTracking: false,
							},
						]
					: []),
				...(shown.has("targets") && targetByMs.size > 0
					? [
							{
								type: "line" as const,
								name: "Block Targets",
								data: days.map((d) => [d.ms, targetByMs.get(d.ms) ?? null]),
								color: VIOLET,
								lineWidth: 2,
								marker: { enabled: true, radius: 3 },
								tooltip: { pointFormat: "Block target: <b>{point.y}</b>" },
								zIndex: 5,
							},
						]
					: []),
			],
		};
	}, [progress, dateWindow, ranges, entry, label, shown, targetByMs, markers]);

	// Best day per phase vs its exit threshold — slim progress rows (replaced the Highcharts
	// bullet chart). Bar color mirrors the daily chart: green once the threshold is met.
	const bestRows = useMemo(() => {
		if (!progress) return [];
		return scopedRanges
			.filter((r) => entry.thresholds[r.phase.id] != null)
			.map((r) => {
				const th = entry.thresholds[r.phase.id];
				const values = progress.dailyValues
					.filter((d) => d.value != null && inWindow(d.date, { from: r.start, to: r.end }) && inWindow(d.date, dateWindow))
					.map((d) => d.value as number);
				const y = values.length > 0 ? Math.max(...values) : 0;
				const met = values.length > 0 && (th.comparison === "<=" ? y <= th.threshold : y >= th.threshold);
				return { name: r.phase.name, y, target: th.threshold, met };
			});
	}, [progress, scopedRanges, dateWindow, entry]);

	return (
		<Card
			className="rtp-prog-card"
			data-metric-key={entry.key}
			title={label}
		>
			{!progress ? (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Couldn't load data for this metric." />
			) : (
				<>
					{/* The chart's legend: click an item to hide or show its line. */}
					<div className="rtp-prog-legend">
						{legend.map((item) => (
							<button
								key={item.key}
								type="button"
								className={`rtp-prog-legend-item${shown.has(item.key) ? "" : " is-off"}`}
								aria-pressed={shown.has(item.key)}
								data-attr="settings.rtp-progress.toggle-line"
								onClick={() => toggleLine(item.key, !shown.has(item.key))}
							>
								<span
									className={`rtp-prog-swatch${item.dashed ? " rtp-prog-swatch--dashed" : ""}`}
									style={{ borderColor: shown.has(item.key) ? item.color : "#d9d9d9" }}
								/>
								{item.label}
							</button>
						))}
					</div>
					{dailyOptions ? (
						<HighchartsReact highcharts={Highcharts} options={dailyOptions} />
					) : (
						<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data in this range yet." />
					)}
					{bestRows.length > 0 ? (
						<div className="rtp-prog-bullet">
							<Text type="secondary" className="rtp-prog-section-label">
								Best day vs exit threshold
							</Text>
							{bestRows.map((row) => (
								<div key={row.name} className="rtp-prog-best-row">
									<span className="rtp-prog-best-name">{row.name}</span>
									<Progress
										percent={Math.min(100, Math.round((row.y / row.target) * 100))}
										size="small"
										strokeColor={row.met ? SUCCESS : ACCENT}
										format={() => `${formatStat(row.y)} / ${formatStat(row.target)}`}
									/>
								</div>
							))}
						</div>
					) : null}
				</>
			)}
		</Card>
	);
};

interface ProgressTabProps {
	plan: RtpPlanDetail;
	// Fetched by PlanDetail on plan load (prefetch), not here — see useRtpMetricProgress.
	progressByKey: Record<string, RtpMetricProgress>;
	progressLoading: boolean;
}

export const ProgressTab = ({ plan, progressByKey, progressLoading }: ProgressTabProps) => {
	const { byKey: metricByKey } = useRtpMetricOptions();
	const ranges = useMemo(() => phaseDateRanges(plan), [plan]);

	const metricEntries = useMemo<MetricEntry[]>(() => {
		const order: MetricEntry[] = [];
		const byKey: Record<string, MetricEntry> = {};
		for (const phase of plan.phases) {
			for (const criterion of phase.criteria) {
				const key = criterion.details.metricKey;
				if (criterion.criterionType !== "metric" || !key) continue;
				let entry = byKey[key];
				if (!entry) {
					entry = { key, thresholds: {} };
					byKey[key] = entry;
					order.push(entry);
				}
				// ponytail: first metric criterion per phase wins — two on the same metric in one phase is degenerate
				if (entry.thresholds[phase.id] == null && criterion.details.threshold != null) {
					entry.thresholds[phase.id] = {
						threshold: criterion.details.threshold,
						comparison: criterion.details.comparison ?? ">=",
					};
				}
			}
		}
		return order;
	}, [plan]);

	const [phaseId, setPhaseId] = useState<number | null>(null);
	const [weekIdx, setWeekIdx] = useState<number | null>(null);

	if (metricEntries.length === 0) {
		return <Empty description="No metric exit criteria in this plan yet — add one to track progress here." />;
	}
	if (plan.status === "draft" || ranges.length === 0) {
		return <Empty description="Data appears once the plan starts." />;
	}

	const selectedRange = ranges.find((r) => r.phase.id === phaseId) ?? null;
	const weeks = selectedRange
		? buildWeeks(selectedRange.phase, selectedRange.end.diff(selectedRange.start, "day") + 1)
		: [];
	const dateWindow: DateWindow | null = selectedRange
		? (weekIdx != null && weeks[weekIdx] ? weekWindow(weeks[weekIdx]) : null) ?? {
				from: selectedRange.start,
				to: selectedRange.end,
			}
		: null;

	return (
		<div className="rtp-prog">
			<div className="rtp-pills">
				{ranges.map((r) => (
					<Tag.CheckableTag
						key={r.phase.id}
						checked={phaseId === r.phase.id}
						onChange={() => {
							setWeekIdx(null);
							setPhaseId(phaseId === r.phase.id ? null : r.phase.id);
						}}
					>
						{r.phase.name}
					</Tag.CheckableTag>
				))}
			</div>
			{selectedRange && weeks.length > 1 ? (
				<div className="rtp-pills rtp-prog-week-pills">
					{weeks.map((cells, i) => {
						const win = weekWindow(cells);
						if (!win) return null;
						return (
							<Tag.CheckableTag
								key={i}
								checked={weekIdx === i}
								onChange={() => setWeekIdx(weekIdx === i ? null : i)}
							>
								{win.from.format("MMM D")} – {win.to.format("MMM D")}
							</Tag.CheckableTag>
						);
					})}
				</div>
			) : null}

			<div className="rtp-prog-cards">
				{metricEntries.map((entry) =>
					progressLoading ? (
						<Card key={entry.key} className="rtp-prog-card" data-metric-key={entry.key}>
							<Skeleton active paragraph={{ rows: 4 }} />
						</Card>
					) : (
						<MetricProgressCard
							key={entry.key}
							entry={entry}
							label={metricByKey[entry.key]?.label ?? entry.key}
							ranges={ranges}
							dateWindow={dateWindow}
							progress={progressByKey[entry.key]}
						/>
					)
				)}
			</div>
		</div>
	);
};
